/**
 * Ctrl/Cmd+K: find a chat by its title. The prototype's 560px card over a
 * scrim, arrow keys and Enter to open, Escape to close.
 *
 * Titles only, from the list the sidebar already holds — titles are encrypted
 * at rest like everything else, so the Worker cannot search them for us
 * without decrypting every chat, and the list is already here decrypted.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useRouter } from '../router';
import { useApp } from '../state';
import { Ph } from './ui';

export function SearchDialog({ onClose }: { onClose: () => void }) {
  const { chats, projects } = useApp();
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const list = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return chats
      .filter((c) => (c.title || 'New chat').toLowerCase().includes(needle))
      .map((c) => ({
        id: c.id,
        title: c.title || 'New chat',
        project: projects.find((p) => p.id === c.projectId)?.name ?? ''
      }));
  }, [chats, projects, q]);

  useEffect(() => setActive(0), [q]);

  // Keep the highlighted row in view as the arrows move past the fold.
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const open = (id: string) => {
    onClose();
    navigate(`/chat/${id}`);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!results.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[active];
      if (hit) open(hit.id);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        background: 'rgba(8,6,16,.45)',
        display: 'flex',
        justifyContent: 'center',
        padding: '12vh 16px 0'
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search chats"
        className="fade-in"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{
          width: 560,
          maxWidth: '100%',
          alignSelf: 'flex-start',
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-dialog)',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 16px',
            height: 52,
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <Ph name="magnifying-glass" size={17} color="var(--text-muted)" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats"
            aria-label="Search chats"
            role="combobox"
            aria-expanded="true"
            aria-controls="assist-search-results"
            aria-activedescendant={results[active] ? `assist-search-${results[active]!.id}` : undefined}
            autoFocus
            style={{
              flex: 1,
              border: 0,
              background: 'transparent',
              fontSize: 15,
              color: 'var(--text-strong)',
              boxShadow: 'none'
            }}
          />
        </div>
        <div
          ref={list}
          id="assist-search-results"
          role="listbox"
          className="scroll"
          style={{ padding: 6, maxHeight: 360, overflow: 'auto' }}
        >
          {results.map((r, i) => (
            <button
              key={r.id}
              id={`assist-search-${r.id}`}
              data-index={i}
              type="button"
              role="option"
              aria-selected={i === active}
              tabIndex={-1}
              className="bare"
              onClick={() => open(r.id)}
              onMouseMove={() => setActive(i)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 44,
                padding: '0 10px',
                borderRadius: 6,
                background: i === active ? 'var(--border-subtle)' : 'transparent'
              }}
            >
              <Ph name="chat-circle-dots" size={15} color="var(--text-muted)" />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13.5,
                  color: 'var(--text-strong)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {r.title}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{r.project}</span>
            </button>
          ))}
          {!results.length ? (
            <div style={{ padding: '12px 10px', fontSize: 13.5, color: 'var(--text-muted)' }}>
              {chats.length ? 'No chats match.' : 'No chats yet.'}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
